import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
} from "../ui";
import {
  AlertTriangle,
  RefreshCw,
  Bug,
  Home,
  Mail,
  ChevronDown,
  Copy,
  CheckCircle,
} from "lucide-react";
import { cn } from "../../lib/utils";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  showDetails: boolean;
  copied: boolean;
}

class InsightErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      showDetails: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const eventId = `insight_error_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return {
      hasError: true,
      error,
      eventId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Insight Error Boundary caught an error:", error, errorInfo);

    this.setState({
      errorInfo,
    });

    // Call onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report error to monitoring service (e.g., Sentry)
    this.reportError(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error state when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys !== resetKeys) {
      if (
        resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index])
      ) {
        this.resetErrorBoundary();
      }
    }

    // Reset error state when any props change (if enabled)
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      // This would typically send to an error reporting service
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventId: this.state.eventId,
        userId: localStorage.getItem("userId"), // If available
      };

      // Send to your error reporting service
      await fetch("/api/errors/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(errorReport),
      }).catch(() => {
        // Silently fail if error reporting fails
      });
    } catch {
      // Ignore errors in error reporting
    }
  };

  private resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      showDetails: false,
      copied: false,
    });
  };

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  private copyError = async () => {
    const { error, errorInfo, eventId } = this.state;

    const errorText = `
Error ID: ${eventId}
Message: ${error?.message}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copied: true });

      setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = errorText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      this.setState({ copied: true });
      setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    }
  };

  private refreshPage = () => {
    window.location.reload();
  };

  private goHome = () => {
    window.location.href = "/";
  };

  private reportBug = () => {
    const { eventId } = this.state;
    const subject = encodeURIComponent(`Bug Report - Error ID: ${eventId}`);
    const body = encodeURIComponent(`
I encountered an error in the insights dashboard.

Error ID: ${eventId}
URL: ${window.location.href}
Time: ${new Date().toISOString()}

Please describe what you were doing when the error occurred:


Additional details:

    `);

    window.open(`mailto:support@yourapp.com?subject=${subject}&body=${body}`);
  };

  render() {
    const { hasError, error, errorInfo, eventId, showDetails, copied } =
      this.state;
    const {
      children,
      fallback,
      showDetails: showDetailsProp = true,
    } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Something went wrong</span>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  We encountered an unexpected error while loading your
                  insights. This has been automatically reported to our team.
                </AlertDescription>
              </Alert>

              {/* Error ID */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Error ID:
                    </span>
                    <code className="ml-2 text-sm font-mono text-gray-900">
                      {eventId}
                    </code>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={this.copyError}
                    className="h-8"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Details
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Error Details (Collapsible) */}
              {showDetailsProp && (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={this.toggleDetails}
                    className="w-full justify-between"
                  >
                    <span>Technical Details</span>
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform",
                        showDetails && "rotate-180"
                      )}
                    />
                  </Button>

                  {showDetails && (
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">
                            Error Message:
                          </span>
                          <pre className="mt-1 p-2 bg-white rounded border text-red-600 whitespace-pre-wrap">
                            {error?.message}
                          </pre>
                        </div>

                        {error?.stack && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Stack Trace:
                            </span>
                            <pre className="mt-1 p-2 bg-white rounded border text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {error.stack}
                            </pre>
                          </div>
                        )}

                        {errorInfo?.componentStack && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Component Stack:
                            </span>
                            <pre className="mt-1 p-2 bg-white rounded border text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {errorInfo.componentStack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button onClick={this.resetErrorBoundary} className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>

                <Button
                  variant="outline"
                  onClick={this.refreshPage}
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Page
                </Button>

                <Button
                  variant="outline"
                  onClick={this.goHome}
                  className="flex-1"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              </div>

              {/* Support Actions */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="link"
                  size="sm"
                  onClick={this.reportBug}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Bug className="w-4 h-4 mr-1" />
                  Report Bug
                </Button>
              </div>

              {/* Help Text */}
              <div className="text-xs text-center text-gray-500 pt-2 border-t">
                If this problem persists, please contact support with the error
                ID above.
                <br />
                You can also try refreshing the page or clearing your browser
                cache.
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

export default InsightErrorBoundary;

// Helper HOC for wrapping components with error boundary
export function withInsightErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, "children">
) {
  const WrappedComponent = (props: P) => (
    <InsightErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </InsightErrorBoundary>
  );

  WrappedComponent.displayName = `withInsightErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}

// Error boundary specifically for insight cards
export const InsightCardErrorBoundary: React.FC<{
  children: ReactNode;
  insightId?: string;
}> = ({ children, insightId }) => (
  <InsightErrorBoundary
    showDetails={false}
    onError={(error, errorInfo) => {
      console.error(`Error in insight card ${insightId}:`, error, errorInfo);
    }}
    fallback={
      <Card className="w-full border-red-200 bg-red-50">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700 mb-3">Failed to load insight</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    }
  >
    {children}
  </InsightErrorBoundary>
);
